import { defaultSkill } from "@izicvs/schema";
import { Badge, Button } from "@izicvs/ui";
import { cn } from "@izicvs/utils";
import { i18n } from "@lingui/core";
import { t } from "@lingui/macro";
import { createId } from "@paralleldrive/cuid2";
import { Check, ChevronDown, Crown, Loader2, Lock, Plus, Sparkles, X } from "lucide-react";
import { useMemo, useState } from "react";

import { toast } from "@/client/hooks/use-toast";
import { analyzeReadiness } from "@/client/services/ai/ats-readiness";
import type { AtsCategoryKey, CareerAnalysis } from "@/client/services/ai/job-match";
import { resumeToPlainText, useAnalyzeResume } from "@/client/services/ai/job-match";
import { useCheckout, useSubscription } from "@/client/services/billing";
import { useResumeStore } from "@/client/stores/resume";

import { SectionIcon } from "../shared/section-icon";
import { InterviewPractice } from "./interview-practice";

const PLAN_ORDER: Record<string, number> = { free: 0, pro: 1, autopilot: 2 };

const scoreText = (score: number) =>
  score >= 75 ? "text-success" : score >= 50 ? "text-warning" : "text-error";

const scoreBar = (score: number) =>
  score >= 75 ? "bg-success" : score >= 50 ? "bg-warning" : "bg-error";

const Meter = ({ label, score }: { label: string; score: number }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between text-xs">
      <span className="text-foreground/70">{label}</span>
      <span className="font-semibold">{Math.round(score)}</span>
    </div>
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
      <div
        className={cn("h-full rounded-full transition-all", scoreBar(score))}
        style={{ width: `${Math.min(Math.max(score, 0), 100)}%` }}
      />
    </div>
  </div>
);

const Block = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="space-y-2">
    <h4 className="text-xs font-bold uppercase tracking-wide text-foreground/60">{title}</h4>
    {children}
  </div>
);

export const AtsSection = () => {
  const data = useResumeStore((state) => state.resume.data);
  const resumeId = useResumeStore((state) => state.resume.id);
  const resumeTitle = useResumeStore((state) => state.resume.title);
  const setValue = useResumeStore((state) => state.setValue);

  const { subscription } = useSubscription();
  const { checkout } = useCheckout();
  const { analyzeResume, loading } = useAnalyzeResume();

  const [jobDescription, setJobDescription] = useState("");
  const [result, setResult] = useState<CareerAnalysis | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const readiness = useMemo(() => analyzeReadiness(data), [data]);

  const billingEnabled = subscription?.billingEnabled ?? false;
  const meetsPro = (PLAN_ORDER[subscription?.plan ?? "free"] ?? 0) >= PLAN_ORDER.pro;
  const locked = billingEnabled && !meetsPro;

  const categoryLabels: Record<AtsCategoryKey, string> = {
    keywords: t`Keywords`,
    preferred: t`Preferred keywords`,
    contact: t`Contact details`,
    quantification: t`Quantified impact`,
    length: t`Length`,
    verbs: t`Action verbs`,
  };

  const onAnalyze = async () => {
    if (jobDescription.trim().length < 40) {
      toast({ variant: "warning", title: t`Paste the full job description to analyze.` });
      return;
    }

    try {
      const analysis = await analyzeResume({
        resume: resumeToPlainText(data),
        jobDescription,
        locale: i18n.locale,
        resumeId,
        resumeTitle,
      });
      setResult(analysis);
    } catch (error) {
      toast({ variant: "error", title: t`Analysis failed`, description: (error as Error).message });
    }
  };

  const addKeyword = (keyword: string) => {
    const skills = data.sections.skills;
    const exists = skills.items.some(
      (item) => item.name.trim().toLowerCase() === keyword.trim().toLowerCase(),
    );
    if (exists) {
      toast({ variant: "info", title: t`"${keyword}" is already in your skills.` });
      return;
    }

    setValue("sections.skills.items", [
      ...skills.items,
      { ...defaultSkill, id: createId(), name: keyword },
    ]);
    toast({ variant: "success", title: t`Added "${keyword}" to your skills.` });

    // Reflect the change locally: move it from missing to matched.
    setResult((prev) =>
      prev
        ? {
            ...prev,
            ats: {
              ...prev.ats,
              missingKeywords: prev.ats.missingKeywords.filter((k) => k !== keyword),
              matchedKeywords: [...prev.ats.matchedKeywords, keyword],
            },
          }
        : prev,
    );
  };

  const upsell = () => {
    void checkout("pro").catch(() => {
      /* redirect handled by the hook */
    });
  };

  const readinessChecks = [
    {
      key: "email",
      ok: readiness.hasEmail,
      label: t`Email address detected`,
      hint: t`Add your email in Basics.`,
    },
    {
      key: "phone",
      ok: readiness.hasPhone,
      label: t`Phone number detected`,
      hint: t`Add your phone in Basics.`,
    },
    {
      key: "sections",
      ok: readiness.hasExperience && readiness.hasEducation && readiness.hasSkills,
      label: t`Standard sections present`,
      hint: t`Include experience, education and skills sections.`,
    },
    {
      key: "metrics",
      ok: readiness.metrics >= 3,
      label: t`Quantified results (${readiness.metrics})`,
      hint: t`Add numbers, % and $ to show impact.`,
    },
    {
      key: "verbs",
      ok: readiness.actionVerbs >= 4,
      label: t`Strong action verbs (${readiness.actionVerbs})`,
      hint: t`Start bullets with verbs like led, built, increased.`,
    },
    {
      key: "length",
      ok: readiness.wordCount >= 150 && readiness.wordCount <= 1000,
      label: t`Length: ${readiness.wordCount} words`,
      hint:
        readiness.wordCount < 150
          ? t`Too short: aim for 150 to 1000 words.`
          : t`Too long: trim toward 1000 words.`,
    },
  ];

  return (
    <section id="ats" className="grid gap-y-6">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-x-4">
          <SectionIcon id="ats" size={18} name={t`ATS Analysis`} />
          <h2 className="line-clamp-1 text-2xl font-bold lg:text-3xl">{t`ATS Analysis`}</h2>
        </div>
      </header>

      {/* ATS readiness: always-on, free, instant (no AI, updates live) */}
      <div className="grid gap-y-4">
        <div className="flex items-center gap-x-4 rounded-md border p-4">
          <div className={cn("text-4xl font-bold tabular-nums", scoreText(readiness.score))}>
            {readiness.score}
          </div>
          <div className="grid gap-y-0.5">
            <span className="text-sm font-semibold">{t`ATS readiness`}</span>
            <span className="text-xs text-foreground/60">
              {t`How machine-readable your resume is. Updates as you edit.`}
            </span>
          </div>
        </div>

        <div className="grid gap-y-1.5">
          {readinessChecks.map((check) => (
            <div key={check.key} className="flex items-start gap-x-2 text-xs">
              {check.ok ? (
                <Check className="mt-0.5 size-3.5 shrink-0 text-success" />
              ) : (
                <X className="mt-0.5 size-3.5 shrink-0 text-error" />
              )}
              <div>
                <span>{check.label}</span>
                {!check.ok && <p className="text-foreground/50">{check.hint}</p>}
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          className="flex items-center gap-x-1 text-xs text-foreground/60 hover:text-foreground"
          onClick={() => {
            setShowPreview((value) => !value);
          }}
        >
          <ChevronDown
            className={cn("size-3.5 transition-transform", showPreview && "rotate-180")}
          />
          {t`What the ATS reads`}
        </button>
        {showPreview && (
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-md border bg-secondary/40 p-3 text-[11px] leading-relaxed text-foreground/70">
            {readiness.preview || t`Your resume is empty.`}
          </pre>
        )}
      </div>

      <div className="h-px w-full bg-border" />

      <h3 className="text-sm font-bold">{t`Match against a job`}</h3>

      {locked ? (
        <div className="flex flex-col items-start gap-y-3 rounded-md border border-dashed p-4">
          <div className="flex items-center gap-x-2 font-semibold">
            <Lock className="size-4" />
            {t`Available on the Pro plan`}
          </div>
          <p className="text-sm text-foreground/70">
            {t`Analyze your resume against any job description: ATS score, missing keywords, a recruiter's verdict and interview prep.`}
          </p>
          <Button size="sm" onClick={upsell}>
            <Crown className="mr-2 size-4" />
            {t`Upgrade to Pro`}
          </Button>
        </div>
      ) : (
        <div className="grid gap-y-4">
          <p className="text-sm leading-relaxed text-foreground/70">
            {t`Paste a job description to score how well this resume matches it and get concrete, ATS-friendly fixes.`}
          </p>

          <textarea
            value={jobDescription}
            rows={6}
            placeholder={t`Paste the job description here...`}
            className="w-full resize-y rounded-md border bg-background p-3 text-sm outline-none ring-primary focus:ring-1"
            onChange={(event) => {
              setJobDescription(event.target.value);
            }}
          />

          <Button disabled={loading} onClick={onAnalyze}>
            {loading ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 size-4" />
            )}
            {loading ? t`Analyzing...` : t`Analyze`}
          </Button>

          {result && (
            <div className="grid gap-y-6">
              {/* Score */}
              <div className="flex items-center gap-x-4 rounded-md border p-4">
                <div className={cn("text-4xl font-bold tabular-nums", scoreText(result.ats.score))}>
                  {Math.round(result.ats.score)}
                </div>
                <div className="grid gap-y-0.5">
                  <span className="text-sm font-semibold">{t`ATS match score`}</span>
                  <span className="text-xs text-foreground/60">
                    {result.ats.stats.matchedRequired}/{result.ats.stats.requiredCount}{" "}
                    {t`required keywords matched`}
                  </span>
                </div>
              </div>

              {/* Category breakdown */}
              <Block title={t`Breakdown`}>
                <div className="grid gap-y-2.5">
                  {result.ats.categories.map((category) => (
                    <Meter
                      key={category.key}
                      label={categoryLabels[category.key] ?? category.key}
                      score={category.score}
                    />
                  ))}
                </div>
              </Block>

              {/* Missing keywords → click to add */}
              {result.ats.missingKeywords.length > 0 && (
                <Block title={t`Missing keywords`}>
                  <p className="text-xs text-foreground/60">
                    {t`Click a keyword to add it to your Skills section (visible and ATS-readable).`}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {result.ats.missingKeywords.map((keyword) => (
                      <button
                        key={keyword}
                        type="button"
                        className="group inline-flex items-center gap-1 rounded-full border border-error/40 bg-error/5 px-2.5 py-1 text-xs text-error transition-colors hover:bg-error/10"
                        onClick={() => {
                          addKeyword(keyword);
                        }}
                      >
                        <Plus className="size-3 opacity-70 group-hover:opacity-100" />
                        {keyword}
                      </button>
                    ))}
                  </div>
                </Block>
              )}

              {/* Matched keywords */}
              {result.ats.matchedKeywords.length > 0 && (
                <Block title={t`Matched keywords`}>
                  <div className="flex flex-wrap gap-2">
                    {result.ats.matchedKeywords.map((keyword) => (
                      <Badge
                        key={keyword}
                        outline
                        variant="success"
                        className="text-xs font-normal"
                      >
                        {keyword}
                      </Badge>
                    ))}
                  </div>
                </Block>
              )}

              {/* Recruiter verdict */}
              <Block title={t`Recruiter's verdict`}>
                <div className="flex items-center gap-x-2">
                  <Badge
                    variant={
                      result.recruiter.shortlist === "yes"
                        ? "success"
                        : result.recruiter.shortlist === "maybe"
                          ? "warning"
                          : "error"
                    }
                    className="font-bold uppercase"
                  >
                    {result.recruiter.shortlist === "yes"
                      ? t`Shortlist`
                      : result.recruiter.shortlist === "maybe"
                        ? t`Maybe`
                        : t`Pass`}
                  </Badge>
                </div>
                <p className="text-sm leading-relaxed">{result.recruiter.verdict}</p>
                {result.recruiter.concerns.length > 0 && (
                  <ul className="ml-4 list-disc space-y-1 text-xs text-foreground/70">
                    {result.recruiter.concerns.map((concern, index) => (
                      <li key={index}>{concern}</li>
                    ))}
                  </ul>
                )}
              </Block>

              {/* Section feedback (actionable edits) */}
              {result.analysis.sectionFeedback.length > 0 && (
                <Block title={t`Suggested improvements`}>
                  <div className="grid gap-y-2">
                    {result.analysis.sectionFeedback.map((item, index) => (
                      <div key={index} className="rounded-md border p-2.5 text-xs">
                        <span className="font-semibold">{item.section}</span>
                        <p className="mt-0.5 text-foreground/70">{item.feedback}</p>
                      </div>
                    ))}
                  </div>
                </Block>
              )}

              {/* Interview practice: answer the predicted questions, get STAR feedback */}
              {result.interview.questions.length > 0 && (
                <Block title={t`Interview practice`}>
                  <InterviewPractice
                    questions={result.interview.questions}
                    resume={resumeToPlainText(data)}
                    jobDescription={jobDescription}
                  />
                </Block>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
};
